import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import isEmail from 'validator/es/lib/isEmail';
import * as Yup from 'yup';
import { Button, FieldWrapper, Form, Identity, Input, ModalContent, ModalFooter, ModalHeader, ModalTitle, Select } from 'components';
import { JobTitleCreatableSelect } from 'components/JobTitleCreatableSelect/JobTitleCreatableSelect';
import { DDOption, DisbursementOptions } from 'config/const';
import { theme, toRem } from 'design';
import validations from 'lib/Validations';
import { clearRequest, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { addEmployeeRequest, Employee as EmployeeActions } from 'store/Employee';
import { IEmployerState, IStore } from 'store/types';
import { EmployeeRole, EmployeeType, IEmployee, IEmployerSite, IPOSProvider, ISiteIntegration, SiteRole } from 'types';
import { formatIntegrationName } from 'utils';
import { getDefaultDisbursementMethod, isEmployeeIdRequired } from 'utils/site';
import { beginsWithVowel, stripSpaces } from 'utils/strings';
import { Layout, StyledSubtitle, Wrapper } from '../styles';
import DuplicateUserAlert from './DuplicateUserAlert';

interface IInviteEntity {
  email?: string;
  firstName?: string;
  id?: string;
  jobTitle?: string;
  lastName?: string;
  phone?: string;
  site?: DDOption;
  role?: EmployeeRole;
  disbursementMethod?: DDOption;
  inviteRecipient?: string;
  customerEmployeeId?: string;
  schedules?: Array<any>;
}

interface IInviteNewProps {
  dataCy: string;
  provider: IPOSProvider;
  site: IEmployerSite;
  org: IEmployerState;
  integration: ISiteIntegration;
  onClose?: () => void;
  onSecondaryAction?: () => void;
  onPrimaryAction?: (employee: IEmployee) => void;
}

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(theme.space.xl)};
  padding: ${toRem(theme.space.lg)};
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.medium};
  border-radius: ${toRem(theme.borderRadius.small)};
  background: ${theme.color.warning[25]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space.lg)};
  width: 100%;
  margin-bottom: ${toRem(theme.space.xl)};

  @media (min-width: 470px) {
    grid-template-columns: 1fr 1fr;
    margin-bottom: 0;

    &.full {
      grid-template-columns: 1fr;
      margin-bottom: ${toRem(theme.space.xl)};
    }
  }

  &.full {
    grid-template-columns: 1fr;
    margin-bottom: ${toRem(theme.space.xl)};
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${toRem(theme.space.xl)};
  background: ${theme.color.neutral[50]};
  border-radius: ${toRem(theme.borderRadius.small)};
`;

const StyledFieldWrapper = styled(FieldWrapper)`
  display: flex;
  flex-direction: column;

  header {
    padding-bottom: ${toRem(0)};
  }

  @media (min-width: 470px) {
    margin-bottom: ${toRem(theme.space['2xl'])};
  }
`;

const InviteModalFooter = styled(ModalFooter)`
  padding-top: ${toRem(theme.space.xl)};

  button:first-child,
  button:last-child {
    justify-self: stretch;
  }
`;

type ModalTypes = undefined | 'inviteUser' | 'duplicateUser';

const InviteNew: FC<IInviteNewProps> = ({ dataCy, provider, site, org, integration, onClose, onPrimaryAction, onSecondaryAction }) => {
  const dispatch = useDispatch();
  const [modalType, setModalType] = useState<ModalTypes>(undefined);
  const [duplicateEmployee, setDuplicateEmployee] = useState<IEmployee>();
  const isAddUserLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, EmployeeActions.ADD_EMPLOYEE_REQUEST));

  const validationSchema = Yup.object().shape({
    id: validations.employeeIdValidationSchema([site]),
    firstName: validations.firstNameValidationSchema,
    lastName: validations.lastNameValidationSchema,
    jobTitle: Yup.string().trim().required('Job title is required'),
    inviteRecipient: Yup.string()
      .test('IsNotEmpty', 'You must enter an email address', (identity: string) => !isEmpty(identity))
      .test('IsIdentity', 'Invalid email address', (identity: string) => !isEmpty(identity) && isEmail(identity || '')),
  });

  const fk = useFormik({
    initialValues: {
      firstName: provider?.firstName || '',
      lastName: provider?.lastName || '',
      id: provider?.customerEmployeeId || '',
      site: { value: site.id, label: site.name },
      disbursementMethod: getDefaultDisbursementMethod(site.id, [site]),
      inviteRecipient: provider?.email?.toLowerCase() || '',
      jobTitle: provider?.schedules[0]?.jobTitle,
    },
    onSubmit: (vals: IInviteEntity) => {
      const payload = {
        firstName: stripSpaces(vals.firstName),
        lastName: stripSpaces(vals.lastName),
        inviteRecipient: vals.inviteRecipient,
        organizationId: org.id,
        integrations: [
          {
            id: provider.employeeId,
            siteId: site.id,
            type: integration.type,
          },
        ],
        type: EmployeeType.USER,
        sites: [
          {
            role: SiteRole.EMPLOYEE,
            id: site.id,
            ...(vals.id?.trim() ? { employeeId: vals.id } : {}),
            job: { title: vals.jobTitle },
            disbursementMethod: vals.disbursementMethod?.value,
          },
        ],
      };
      dispatch(
        addEmployeeRequest(payload, {
          onSuccess: (emp) => onPrimaryAction?.(emp),
          onDuplicate: (dupEmp) => {
            setDuplicateEmployee(dupEmp);
            setModalType('duplicateUser');
          },
          preventToast: true,
        })
      );
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  const { touched, errors, getFieldProps, handleBlur, handleSubmit, setFieldValue, values, setFieldTouched } = fk;

  useEffect(() => () => dispatch(clearRequest(EmployeeActions.ADD_EMPLOYEE_REQUEST)), []);

  const validateField = (field: 'id' | 'firstName' | 'lastName' | 'jobTitle' | 'disbursementMethod' | 'inviteRecipient') => Boolean(touched[field] && errors[field]);

  const modalTitle = useMemo(() => {
    const name = formatIntegrationName(integration);
    if (beginsWithVowel(name)) return `Invite & Connect an ${name} Employee To Kickfin`;
    return `Invite & Connect a ${name} Employee To Kickfin`;
  }, [integration.type]);

  if (modalType === 'duplicateUser') {
    return (
      <DuplicateUserAlert
        employee={duplicateEmployee}
        email={values.inviteRecipient}
        site={site}
        values={{
          role: EmployeeType.USER,
          job: { title: values.jobTitle },
          disbursementMethod: values.disbursementMethod.value,
          employeeId: values.id,
          integration: {
            id: provider.employeeId,
            siteId: site.id,
            type: integration.type,
          },
        }}
        onPrimaryAction={onPrimaryAction}
        onClose={() => setModalType(undefined)}
      />
    );
  }

  return (
    <Layout isActive onClose={onClose} data-cy={`${dataCy}-invite`} maxWidth={540}>
      <ModalHeader>
        <ModalTitle data-cy="modal-title">{modalTitle}</ModalTitle>
        <StyledSubtitle data-cy="modal-subtitle">Use the following form to invite and connect your employee to Kickfin.</StyledSubtitle>
      </ModalHeader>
      <ModalContent>
        <Form onSubmit={handleSubmit}>
          {site?.role !== SiteRole.ADMIN && <ErrorContainer data-cy="general-error">You do not have permission to invite new employees. Please contact your site admin to invite new employees.</ErrorContainer>}
          <Wrapper>
            <FormWrapper>
              <FormRow>
                <StyledFieldWrapper data-cy={`${dataCy}-invite-first-name`} label="First Name" message={validateField('firstName') ? (errors.firstName as string) : ''} hasError={validateField('firstName')}>
                  <Input {...getFieldProps('firstName')} id="invite-first-name" disabled={isAddUserLoading} />
                </StyledFieldWrapper>

                <StyledFieldWrapper data-cy={`${dataCy}-invite-last-name`} label="Last Name" message={validateField('lastName') ? (errors.lastName as string) : ''} hasError={validateField('lastName')}>
                  <Input {...getFieldProps('lastName')} id="invite-last-name" disabled={isAddUserLoading} />
                </StyledFieldWrapper>
              </FormRow>

              <FormRow>
                <StyledFieldWrapper data-cy={`${dataCy}-invite-id`} label="Employee ID" optional={!isEmployeeIdRequired(site.id, [site])} message={validateField('id') ? (errors.id as string) : ''} hasError={validateField('id')}>
                  <Input {...getFieldProps('id')} id="invite-id" disabled={isAddUserLoading} maxLength={50} />
                </StyledFieldWrapper>

                <StyledFieldWrapper
                  data-cy={`${dataCy}-invite-disbursement`}
                  label="Disbursement"
                  hasError={validateField('disbursementMethod')}
                  tooltip={
                    <>
                      <b>Instant</b> {' payments will be paid out instantly. '}
                      <b>Payroll</b> {" payments will be sent to your site's payroll system."}
                    </>
                  }
                  id="disbursement"
                >
                  <Select
                    placeholder="Select Disbursement Method"
                    options={DisbursementOptions}
                    onChange={(selected) => {
                      setFieldValue('disbursementMethod', selected?.[0]);
                    }}
                    values={[values.disbursementMethod]}
                    disabled={isAddUserLoading}
                  />
                </StyledFieldWrapper>
              </FormRow>

              <FormRow className="full">
                <StyledFieldWrapper data-cy={`${dataCy}-invite-job-title`} label="Job Title" message={validateField('jobTitle') ? (errors.jobTitle as string) : ''} hasError={validateField('jobTitle')}>
                  <JobTitleCreatableSelect
                    createNewLabel="Add {search} as a new job title"
                    disabled={!site.id || isAddUserLoading}
                    placeholder="Select Job Title"
                    defaultValue={getFieldProps('jobTitle').value}
                    siteId={site.id}
                    onChange={(selected) => {
                      setFieldValue('jobTitle', selected?.value?.title);
                    }}
                    onInputBlur={() => setFieldTouched('jobTitle', true)}
                  />
                </StyledFieldWrapper>
              </FormRow>
              <FormRow className="full">
                <StyledFieldWrapper data-cy={`${dataCy}-invite-invite-method`} label="Email Address" message={validateField('inviteRecipient') ? errors.inviteRecipient : ''} hasError={validateField('inviteRecipient')}>
                  <Identity
                    onBlur={handleBlur}
                    value={values.inviteRecipient}
                    onAccept={(val: string) => {
                      setFieldValue('inviteRecipient', val?.toLowerCase());
                    }}
                    type="text"
                    id="inviteRecipient"
                    placeholder=""
                    disabled={isAddUserLoading}
                  />
                </StyledFieldWrapper>
              </FormRow>
            </FormWrapper>
          </Wrapper>

          <InviteModalFooter columns={2}>
            <Button type="button" data-cy="back-btn" appearance="white" label="Back" onClick={onSecondaryAction} disabled={isAddUserLoading} />
            <Button type="submit" data-cy="invite-btn" label="Invite" disabled={isAddUserLoading || site?.role !== SiteRole.ADMIN} isProcessing={isAddUserLoading} />
          </InviteModalFooter>
        </Form>
      </ModalContent>
    </Layout>
  );
};

export default InviteNew;
